import axios from "axios";

// Mirrors backend/Fugro.LabPro.Api/Endpoints/ApiErrors.cs; the errorCodesContract test
// asserts 3-way set equality with the backend catalogue and the common.json apiErrors
// section, so changing a value here requires the matching backend and catalog changes.
export const ERROR_CODES = [
  "test-read-denied",
  "test-modify-denied",
  "test-create-denied",
  "report-denied",
  "lab-users-manage-denied",
  "lab-settings-modify-denied",
  "not-authorized",
  "test-already-exists",
  "sample-id-belongs-to-another-sample",
  "sample-reference-in-use",
  "laboratory-not-found",
  "processing-test-not-found",
  "comment-required",
  "specimen-reference-required",
  "specimen-reference-in-use",
  "ids-required",
  "attachment-name-empty",
  "attachment-name-reserved",
  "attachment-name-invalid",
  "relationship-self-reference",
  "relationship-missing-targets",
  "test-invalid-status",
  "test-conditions-denied",
  "test-conditions-locked",
  "test-conditions-changed",
  "test-conditions-invalid",
  "test-conditions-not-ready",
  "send-back-invalid-status",
  "too-many-report-tests",
  "too-many-ids",
  "duplicate-emails",
  "report-tests-not-found",
  "fdf-not-supported",
  "fdf-template-missing",
  "processing-data-missing",
  "combined-method-processing-only",
  "processing-test-in-progress",
  "malformed-completion-metadata",
  "acquisition-tests-incomplete",
  "pd-approval-required",
  "retest-no-matching-acquisition-tests",
  "report-container-not-found",
  "report-template-required",
  "report-no-eligible-tests",
] as const;

export type ApiErrorCode = (typeof ERROR_CODES)[number];

// "not-authorized" is stamped by the backend on token/framework-level 403s (no app
// permission check involved) — it is not a feature permission denial.
const PERMISSION_DENIED_CODES: readonly ApiErrorCode[] = [
  "test-read-denied",
  "test-modify-denied",
  "test-create-denied",
  "test-conditions-denied",
  "report-denied",
  "lab-users-manage-denied",
  "lab-settings-modify-denied",
];

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  traceId?: string;
  errorCode?: string;
  args?: Record<string, unknown>;
}

function readString(source: Record<string, unknown>, key: string): string | undefined {
  const value = source[key];
  return typeof value === "string" ? value : undefined;
}

function readArgs(source: Record<string, unknown>): Record<string, unknown> | undefined {
  const value = source.args;
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  return value as Record<string, unknown>;
}

export function getProblemDetails(error: unknown): ProblemDetails | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const data: unknown = error.response?.data;
  if (!data || typeof data !== "object" || Array.isArray(data)) return undefined;

  const source = data as Record<string, unknown>;
  const status = source.status;
  const problem: ProblemDetails = {
    type: readString(source, "type"),
    title: readString(source, "title"),
    status: typeof status === "number" ? status : undefined,
    detail: readString(source, "detail"),
    instance: readString(source, "instance"),
    traceId: readString(source, "traceId"),
    errorCode: readString(source, "errorCode"),
    args: readArgs(source),
  };

  const isProblemShaped =
    problem.type !== undefined ||
    problem.title !== undefined ||
    problem.status !== undefined ||
    problem.detail !== undefined;
  return isProblemShaped ? problem : undefined;
}

export function getErrorStatus(error: unknown): number | undefined {
  return axios.isAxiosError(error) ? error.response?.status : undefined;
}

export function isForbidden(error: unknown): boolean {
  return getErrorStatus(error) === 403;
}

export function hasErrorCode(error: unknown, code: ApiErrorCode): boolean {
  return getProblemDetails(error)?.errorCode === code;
}

export function isPermissionDenied(error: unknown): boolean {
  if (!isForbidden(error)) return false;
  const errorCode = getProblemDetails(error)?.errorCode;
  return PERMISSION_DENIED_CODES.includes(errorCode as ApiErrorCode);
}
